/**
 * i18next setup - English (canonical) + Arabic (MSA), with an html `dir` toggle (docs/06
 * Bilingual/RTL, docs/07 §7). RTL is not optional: layout must survive dir='rtl'. Domain terms
 * in ar.json use the locked glossary translations verbatim (docs/06 Locked glossary terms).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGS = ['en', 'ar'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
const STORAGE_KEY = 'aabar.lang';

function initialLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'ar' ? 'ar' : 'en';
}

/** Apply lang + direction at the document root (docs/06: RTL for Arabic). */
export function applyDirection(lang: Lang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: initialLang(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyDirection(i18n.language as Lang);

export async function setLanguage(lang: Lang): Promise<void> {
  localStorage.setItem(STORAGE_KEY, lang);
  await i18n.changeLanguage(lang);
  applyDirection(lang);
}

export default i18n;
